import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { ForeignLookupFieldsWithDefaults, ForeignLookupSchemaTypes } from "../types/foreignLookup.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ForeignLookup" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ForeignLookup>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ForeignLookup extends SchemaBase<ForeignLookup, ForeignLookupFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public targetId!: string;

    // The field on `targetCollection` that resolves `targetId`: querying that collection for the document
    // whose `targetForeignField` equals `targetId` must return exactly one document — the one referenced.
    // A field only qualifies if it carries a unique index on the target collection; "_id" always does,
    // and alternates like "node_id" must be declared unique there. Required on every variant.
    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public targetForeignField!: string;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public targetCollection!: string;

    static getters = {} as const satisfies Record<string, (self: ForeignLookup) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ForeignLookupSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ForeignLookup, ForeignLookupFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ForeignLookup.dtoValidators = getZodSchemaFromClass(
    ForeignLookup,
) as unknown as DtoValidatorTypes<ForeignLookupSchemaTypes>;
